/**
 * SWISH'IN — Instagram wall.
 *
 * Progressive enhancement for `sections/swishin-ugc.liquid`. The section always
 * renders its manual tiles server-side; this file only *replaces* them once a
 * live Instagram response has been parsed successfully. Anything that goes
 * wrong — no token, an expired token, a network failure, a rate limit, a shape
 * we do not recognise — leaves the manual tiles exactly where they are, so the
 * section can never render empty.
 *
 * Tile markup is mirrored from `blocks/_sw-ugc-item.liquid`. Keep the two in
 * step: same classes, same structure.
 *
 * Endpoint notes:
 *   - `graph.instagram.com/me/media` is the Instagram API with Instagram Login.
 *     The old Basic Display API was shut down in December 2024.
 *   - The token is a long-lived user token. It is visible in the page source,
 *     it is read-only over the account's own media, and it expires after
 *     60 days unless refreshed.
 *   - Every visitor's browser spends the SAME token's rate limit (200 calls per
 *     hour). That is why the response is cached in localStorage — without it a
 *     busy day would exhaust the quota and the wall would fall back to manual.
 */
(() => {
  const ENDPOINT = 'https://graph.instagram.com/me/media';
  const FIELDS = 'id,caption,media_type,media_url,permalink,thumbnail_url,timestamp,username';
  const CACHE_PREFIX = 'sw:ig:';
  const REQUEST_TIMEOUT_MS = 8000;

  /**
   * Instagram hands back URLs from its own CDN. Everything here is written into
   * `src` and `href`, so anything that is not plainly an https URL is dropped
   * rather than trusted — this is third-party data, not our own.
   */
  const httpsOnly = (value) => (typeof value === 'string' && value.startsWith('https://') ? value : '');

  const readCache = (key, maxAgeMs) => {
    if (maxAgeMs <= 0) return null;

    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;

      const cached = JSON.parse(raw);
      if (!cached || !Array.isArray(cached.items)) return null;
      if (Date.now() - cached.savedAt > maxAgeMs) return null;

      return cached.items;
    } catch {
      return null;
    }
  };

  const writeCache = (key, items) => {
    try {
      localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), items }));
    } catch {
      // A full or blocked localStorage is not a reason to fail the feed.
    }
  };

  /** Keeps only the media we can actually draw a tile from. */
  const normalise = (media) => {
    if (!media || typeof media !== 'object') return null;

    const isVideo = media.media_type === 'VIDEO';
    // A reel's `media_url` is the mp4 file, so the still has to come from
    // `thumbnail_url`. Carousels report the first child's image in `media_url`.
    const image = httpsOnly(isVideo ? media.thumbnail_url : media.media_url);
    if (!image) return null;

    return {
      image,
      link: httpsOnly(media.permalink),
      handle: typeof media.username === 'string' && media.username ? `@${media.username}` : '',
      caption: typeof media.caption === 'string' ? media.caption.slice(0, 120) : '',
      isVideo,
    };
  };

  const PLAY_ICON =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M8 5.14v13.72a.5.5 0 0 0 .76.43l11.14-6.86a.5.5 0 0 0 0-.86L8.76 4.71a.5.5 0 0 0-.76.43Z"/></svg>';

  /**
   * Built with DOM calls rather than an innerHTML string: `caption` and
   * `handle` are attacker-controllable in principle (anyone whose post is
   * reposted to this account), and setAttribute/textContent escape by default.
   */
  const buildTile = (item, sizes) => {
    const tile = document.createElement(item.link ? 'a' : 'div');
    tile.className = 'sw-ugc__item sw-media sw-media--zoom';

    if (item.link) {
      tile.href = item.link;
      tile.target = '_blank';
      tile.rel = 'noopener';
    }

    const img = document.createElement('img');
    img.src = item.image;
    img.alt = item.caption || item.handle || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    if (sizes) img.sizes = sizes;
    tile.appendChild(img);

    const scrim = document.createElement('span');
    scrim.className = 'sw-ugc__scrim';
    tile.appendChild(scrim);

    if (item.isVideo) {
      const play = document.createElement('span');
      play.className = 'sw-ugc__play';
      play.setAttribute('aria-hidden', 'true');
      play.innerHTML = PLAY_ICON;
      tile.appendChild(play);
    }

    if (item.handle) {
      const handle = document.createElement('span');
      handle.className = 'sw-ugc__handle';
      handle.textContent = item.handle;
      tile.appendChild(handle);
    }

    return tile;
  };

  class SwInstagramFeed extends HTMLElement {
    connectedCallback() {
      // Custom elements re-run this whenever the theme editor moves the node.
      if (this.dataset.swState) return;

      const token = (this.dataset.token || '').trim();
      if (!token) return;

      this.dataset.swState = 'loading';
      this.load(token);
    }

    async load(token) {
      const limit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 8, 1), 25);
      const cacheKey = `${CACHE_PREFIX}${limit}:${token.slice(-10)}`;
      const maxAgeMs = (parseInt(this.dataset.cacheMinutes, 10) || 0) * 60 * 1000;

      const cached = readCache(cacheKey, maxAgeMs);
      if (cached && cached.length) {
        this.paint(cached);
        this.dataset.swState = 'cached';
        return;
      }

      try {
        const items = await this.fetchMedia(token, limit);
        if (!items.length) throw new Error('no usable media in response');

        writeCache(cacheKey, items);
        this.paint(items);
        this.dataset.swState = 'live';
      } catch (error) {
        // The manual tiles are still in the DOM — this is the fallback path,
        // not a broken state, so it is a warning rather than an error.
        this.dataset.swState = 'fallback';
        console.warn('[swishin] Instagram feed unavailable, showing manual tiles instead.', error);
      }
    }

    async fetchMedia(token, limit) {
      const url = new URL(ENDPOINT);
      url.searchParams.set('fields', FIELDS);
      url.searchParams.set('limit', String(limit));
      url.searchParams.set('access_token', token);

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(url, { signal: controller.signal });
        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          const detail = payload && payload.error ? payload.error.message : `HTTP ${response.status}`;
          throw new Error(detail);
        }

        if (!payload || !Array.isArray(payload.data)) throw new Error('unexpected response shape');

        return payload.data.map(normalise).filter(Boolean).slice(0, limit);
      } finally {
        clearTimeout(timer);
      }
    }

    paint(items) {
      const sizes = this.dataset.sizes || '';
      const fragment = document.createDocumentFragment();

      items.forEach((item) => fragment.appendChild(buildTile(item, sizes)));

      this.replaceChildren(fragment);
    }
  }

  if (!customElements.get('sw-instagram-feed')) {
    customElements.define('sw-instagram-feed', SwInstagramFeed);
  }

  /* ========================================================================
     Inline video tiles
     ------------------------------------------------------------------------
     The markup ships with `preload="none"`, so a tile costs nothing but its
     poster until something here asks it to play. Nothing below is required for
     the section to render — without it every video tile simply stays on its
     cover frame with the play badge showing.
     ======================================================================== */

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover)');

  /** Videos currently rolling, so the tab-switch handler knows what to resume. */
  const playing = new Set();
  let suspended = [];

  const flagTile = (video, isPlaying) => {
    const tile = video.closest('.sw-ugc__item');
    if (!tile) return;

    // Drives the badge fade in CSS. The badge stays put until playback is real,
    // which matters when a browser declines to autoplay.
    if (isPlaying) tile.setAttribute('data-sw-playing', '');
    else tile.removeAttribute('data-sw-playing');
  };

  /**
   * Browsers stop decoding a wall of simultaneous videos and start pausing them
   * "to save power", which looked like the tiles simply refusing to play once
   * the section had more than a handful of reels in it. Keeping only a few
   * rolling at a time is what actually keeps them rolling.
   */
  const MAX_PLAYING = 4;

  const makeRoom = (keep) => {
    while (playing.size >= MAX_PLAYING) {
      const oldest = [...playing].find((video) => video !== keep);
      if (!oldest) return;
      stopVideo(oldest, false);
    }
  };

  const startVideo = (video) => {
    if (!playing.has(video)) makeRoom(video);

    const attempt = video.play();

    // Older browsers return undefined; there is nothing to await there.
    if (!attempt || typeof attempt.then !== 'function') {
      playing.add(video);
      flagTile(video, true);
      return;
    }

    attempt.then(
      () => {
        playing.add(video);
        flagTile(video, true);
      },
      () => {
        // Autoplay refused, or the file failed to load. The poster and badge
        // are already correct, so there is nothing to undo.
        flagTile(video, false);
      }
    );
  };

  const stopVideo = (video, rewind) => {
    video.pause();
    if (rewind) video.currentTime = 0;
    playing.delete(video);
    flagTile(video, false);
  };

  let viewportObserver = null;

  const getViewportObserver = () => {
    if (viewportObserver) return viewportObserver;

    viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) startVideo(entry.target);
          else stopVideo(entry.target, false);
        });
      },
      // Enough of the tile has to be on screen to be worth the bandwidth.
      { threshold: 0.4 }
    );

    return viewportObserver;
  };

  const initVideos = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    root.querySelectorAll('.sw-ugc__grid').forEach((grid) => {
      const mode = grid.dataset.videoPlayback === 'hover' ? 'hover' : 'inview';

      grid.querySelectorAll('video.sw-ugc__video').forEach((video) => {
        if (video.dataset.swBound) return;
        video.dataset.swBound = 'true';

        if (mode === 'hover') {
          // Without a real pointer there is no hover to react to, and tapping
          // belongs to the tile's link. The cover frame is the whole tile.
          if (!canHover.matches) return;

          const tile = video.closest('.sw-ugc__item') || video;
          tile.addEventListener('mouseenter', () => startVideo(video));
          tile.addEventListener('mouseleave', () => stopVideo(video, true));
          return;
        }

        // Motion that starts on its own is exactly what this setting asks us
        // not to do.
        if (prefersReducedMotion.matches) return;

        video.dataset.swMode = 'inview';
        getViewportObserver().observe(video);
      });
    });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Only the scroll-driven ones are ours to restart. A hover video should
      // wait for the pointer to come back.
      suspended = [...playing].filter((video) => video.dataset.swMode === 'inview');
      [...playing].forEach((video) => stopVideo(video, false));
      return;
    }

    suspended.forEach(startVideo);
    suspended = [];
  });

  const whenReady = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };

  whenReady(() => initVideos(document));

  // The theme editor swaps a whole section's markup in place.
  document.addEventListener('shopify:section:load', (event) => initVideos(event.target));
})();
